import React, { useEffect, useState } from "react";
import "./styles.css";
import "../container/styles.css";
import { useLocation, useNavigate } from "react-router-dom";
import PrimaryButton from "../components/PrimaryButton";
import { AppPaths } from "../utils/AppPaths";
import { ReactComponent as Message } from "../assets/ForgotPass.svg";
import TextInput from "../components/TextInput";
import { isValidEmail } from "../utils/utils";
import ApiService from "../api/ApiServices";
import AlertModal from "../components/AlertModal";
import toast from "react-hot-toast";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [emailValidate, setEmailValidate] = useState<boolean>(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [resendingOtp, setResendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertTitle, setAlertTitle] = useState<string>("Alert");
  const [alertMessage, setAlertMessage] = useState<string>("");

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get('id');
  const openAlert = (title: string, message: string) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setShowAlert(true);
  };

  useEffect(() => {
    if (!showOtpModal || resendCountdown <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [showOtpModal, resendCountdown]);

  const renderTopSvg = () => {
    return (
      <>
        <svg
          viewBox="10 -25 350 450"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{
            position: "absolute",
            zIndex: -1,
          }}
        >
          <path
            d="M-64 125L27.3314 101.816C137.894 73.7501 253.837 74.7737 363.886 104.787L438 125"
            stroke="#FFD3F6"
            stroke-width="422"
          />
        </svg>
      </>
    );
  };

  const handleInputChange = (e: string) => {
    setEmailValidate(!isValidEmail(e))
    setInputValue(e)
  };

  const renderHeaderImage = () => {
    return (
      <>
        <div className="relative flex flex-col items-center ">
          <Message />
          {/* <span
            className="absolute text-center "
            style={{
              top: "7vh",
              right: "1vh",
              left: "1vh",
            }}
          >
            <>
              <TypeWriter key={7} text="Forgot your username or" delay={40} />
              <TypeWriter key={7} text="password ? No problem!" delay={40} />
            </>
          </span> */}
        </div>
        <img
          alt=""
          src={require("../assets/Forgot.png")}
          className="onb1-2 mt-0"
        />
      </>
    );
  };

  const sendEmail = async (data: string) => {
    if (sendingOtp) {
      return;
    }

    try {
      setSendingOtp(true);
      const response = await ApiService.sendResetOtp({
        email: data,
      });
      if (response.status) {
        // openAlert("Success", response?.message || "OTP sent successfully");
        setOtpEmail(data);
        setOtpValue("");
        setShowOtpModal(true);
        setResendCountdown(30);
      } else {
        openAlert("Error", response?.message || "Failed to send OTP");
      }
    } catch (error: any) {
      openAlert("Error", error?.message || "Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const resendOtp = async () => {
    if (resendingOtp || resendCountdown > 0 || !otpEmail) {
      return;
    }

    try {
      setResendingOtp(true);
      const response = await ApiService.resendForgotPasswordOtp({
        email: otpEmail,
      });

      if (response?.status) {
        // openAlert("Success", response?.message || "OTP resent successfully");
        toast.success(response?.message || "OTP resent successfully");
        setResendCountdown(30);
      } else {
        openAlert("Error", response?.message || "Unable to resend OTP");
      }
    } catch (error: any) {
      openAlert("Error", error?.message || "Failed to resend OTP");
    } finally {
      setResendingOtp(false);
    }
  };

  const verifyOtp = async () => {
    if (verifyingOtp || otpValue.length !== 4 || !otpEmail) {
      return;
    }

    try {
      setVerifyingOtp(true);
      const payload: any = {
        email: otpEmail,
        otp: otpValue,
      };

      if (id) {
        payload.id = id;
      }

      const response = await ApiService.verifyForgotPasswordOtp(payload);

      if (!response?.status) {
        openAlert("Error", response?.message || "Invalid OTP");
        return;
      }

      const token =
        response?.data?.token ||
        response?.token ||
        (typeof response?.data === "string" ? response.data : "");

      if (!token) {
        openAlert("Error", "OTP verified, but reset token was not returned");
        return;
      }

      setShowOtpModal(false);
      setOtpValue("");
      setInputValue("");
      navigate(`${AppPaths.RESET_PASSWORD}?token=${encodeURIComponent(token)}`);
    } catch (error: any) {
      openAlert("Error", error?.message || "Failed to verify OTP");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const closeOtpModal = () => {
    if (verifyingOtp) {
      return;
    }
    setShowOtpModal(false);
  };

  const handleOtpInput = (value: string) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 4);
    setOtpValue(numericValue);
  };

  return (
    <div className="flex flex-col h-svh relative z-10 items-center">
      {renderTopSvg()}

      <div className="z-11">
        <div
          className="mx-10"
          style={{
            marginTop: "3vh",
          }}
        >
          <div className="flex flex-col justify-center items-center" style={{}}>
            {renderHeaderImage()}
          </div>
          <div className="mt-20">
            <TextInput
              placeholder="Enter email"
              value={inputValue}
              onChange={(val) => handleInputChange(val.target.value)}
              containerStyles="px-3 py-3"
            />
            {emailValidate && <span className="mt-4 flex" style={{ color: 'red' }}>Email address is not valid!</span>}
            <PrimaryButton
              onClick={() => {
                localStorage.setItem('forgotMail', inputValue);
                if (id) {
                  localStorage.setItem('id', id);
                }
                sendEmail(inputValue)
                // navigate(AppPaths.RESET_PASSWORD);
              }}
              disable={emailValidate || !inputValue || sendingOtp}
              text={sendingOtp ? "SENDING..." : "SEND OTP"}
              containerStyles="py-2 mt-5"
              textStyle="inter-bold-text-16-white"
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col justify-center flex-1 mt-5 "></div>

      <AlertModal
        visible={showAlert}
        title={alertTitle}
        message={alertMessage}
        onConfirm={() => setShowAlert(false)}
      />

      {showOtpModal && (
        <div className="ModalOverlay alert-modal-overlay" onClick={closeOtpModal}>
          <div
            className="alert-modal-card"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <h2 className="alert-modal-title">Verify OTP</h2>
            <p className="alert-modal-message" style={{ marginBottom: 12 }}>
              Enter the 4-digit OTP sent to {otpEmail}
            </p>

            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={otpValue}
              onChange={(e) => handleOtpInput(e.target.value)}
              placeholder="0000"
              style={{
                height: 48,
                border: "2px solid #000000",
                borderRadius: 8,
                padding: "0 12px",
                fontSize: 20,
                textAlign: "center",
                letterSpacing: 6,
                fontFamily: "Inter-SemiBold",
                marginBottom: 16,
              }}
            />

            <button
              type="button"
              className="alert-modal-button"
              onClick={verifyOtp}
              disabled={otpValue.length !== 4 || verifyingOtp}
              style={{
                opacity: otpValue.length !== 4 || verifyingOtp ? 0.6 : 1,
                cursor: otpValue.length !== 4 || verifyingOtp ? "not-allowed" : "pointer",
              }}
            >
              {verifyingOtp ? "VERIFYING..." : "VERIFY OTP"}
            </button>

            <div style={{ marginTop: 14, fontFamily: "Inter-Regular", fontSize: 14 }}>
              {resendCountdown > 0 ? (
                <span>Resend OTP in {resendCountdown}s</span>
              ) : (
                <button
                  type="button"
                  onClick={resendOtp}
                  disabled={resendingOtp}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#EA088C",
                    fontFamily: "Inter-Bold",
                    cursor: resendingOtp ? "not-allowed" : "pointer",
                    opacity: resendingOtp ? 0.6 : 1,
                  }}
                >
                  {resendingOtp ? "RESENDING..." : "Resend OTP"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForgotPassword;
